import { ApolloClient, from, HttpLink, InMemoryCache } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

function signout() {
  signOut()
    .then(() => {
      const defaultLanguage = window.localStorage.getItem('defaultLanguage');
      window.localStorage.removeItem('apollo-cache-persist');
      window.localStorage.setItem('defaultLanguage', defaultLanguage || 'en');

      // Dispatch a custom event that React components can listen to
      window.dispatchEvent(
        new CustomEvent('apollo-signout', {
          detail: { reason: 'unauthorized' },
        }),
      );

      // Navigate to signin page using window.location since React Router isn't available
      window.location.href = '/signin';
    })
    .catch((err) => {
      console.error(err);
      // Still navigate to signin even if signOut fails
      window.location.href = '/signin';
    });
}

const httpLink = new HttpLink({
  uri: import.meta.env.VITE_API_SERVER,
  credentials: 'same-origin',
});

const authLink = setContext(async (_, { headers }) => {
  // if you have a cached value, return it immediately
  try {
    const { idToken } = (await fetchAuthSession()).tokens ?? {};

    return {
      headers: {
        ...headers,
        authorization: idToken ? `Bearer ${idToken}` : '',
        'x-app-name': import.meta.env.VITE_APP_NAME,
      },
    };
  } catch (err) {
    console.error('Error fetching auth session', err);
    return {
      headers: {
        ...headers,
        'x-app-name': import.meta.env.VITE_APP_NAME,
      },
    };
  }
});

const errorLink = onError(
  ({ graphQLErrors, networkError, protocolErrors, operation }) => {
    const operationName = operation?.operationName || 'Unknown';

    if (graphQLErrors) {
      for (const { message, locations, path } of graphQLErrors) {
        console.error(
          `[GraphQL error in ${operationName} with variables ${JSON.stringify(operation.variables)}]: Message: ${message}, Location: ${JSON.stringify(locations)}, Path: ${path}`,
        );
      }

      for (const { status = 200 } of graphQLErrors) {
        switch (status) {
          case 401:
            console.warn('Authentication error: User is unauthenticated.');
            signout();
            break;
          case 403:
            console.warn('Authorization error: User is forbidden.');
            break;
          case 200:
            console.warn('Server Application error:', status);
            break;
          default:
            console.warn('Unknown error:', status);
            break;
        }
      }
    }

    if (networkError) {
      console.error(
        `[Network Error in ${operationName} with variables ${JSON.stringify(operation.variables)}]:`,
        networkError,
        networkError.statusCode,
      );

      switch (networkError.statusCode) {
        case 401:
          console.warn('Authentication error: User is unauthenticated.');
          signout();
          break;
        case 403:
          console.warn('Authorization error: User is forbidden.');
          break;
        case 500:
          console.warn('Internal server error:', networkError.statusCode);
          break;
        case 200:
          console.warn('Server Application error:', networkError.statusCode);
          break;
        default:
          console.warn('Unknown Network error:', networkError.statusCode);
          break;
      }
    }

    if (protocolErrors) {
      for (const { message, extensions } of protocolErrors) {
        console.error(
          `[Protocol error in ${operationName} with variables ${JSON.stringify(operation.variables)}]: Message: ${message}, Extensions: ${JSON.stringify(extensions)}`,
        );
      }
    }
  },
);

const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // Add field policies here if needed
        productionOrders: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
    Patient: {
      fields: {
        settingGroupOptions: {
          merge(_existing, incoming) {
            return incoming;
          },
        },
      },
    },
  },
});

const apolloClient = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache,
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
    },
    query: {
      fetchPolicy: 'network-only',
      errorPolicy: 'all',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
  connectToDevTools: true,
});

export default apolloClient;
